import { DialogTitle } from "@headlessui/react";
import { AccessibleIcon } from "../../accessibility";
import { ArrowLeftIcon } from "../../icons/ArrowLeftIcon";
import { CloseIcon } from "../../icons/CloseIcon";
import { Heading } from "../../typography";
import { classNames } from "../../utils";

type ModalTitleSpacing = "none" | "sm" | "md";

const spacingClassNames: Record<ModalTitleSpacing, string | undefined> = {
	none: undefined,
	sm: "mb-2",
	md: "mb-4",
};

type ModalTitleProps = {
	variant?: "default" | "fullScreen";
	spacing?: ModalTitleSpacing;
	icon?: React.ReactNode;
	children: React.ReactNode;
	onClose?: () => void;
	i18n?: {
		closeButton: string;
	};
};

export const ModalTitle = ({ variant = "default", spacing = "md", icon, children, onClose, i18n }: ModalTitleProps) => {
	if (variant === "fullScreen") {
		return (
			<>
				{onClose && i18n && (
					<button type="button" onClick={onClose}>
						<FullScreenModalCloseContent i18n={i18n} />
					</button>
				)}
				<div className="space-y-6">
					<DialogTitle as="div">
						<FullScreenModalTitleContent icon={icon} isModal>
							{children}
						</FullScreenModalTitleContent>
					</DialogTitle>
					<FullScreenModalSeparator />
				</div>
			</>
		);
	}

	return (
		<DialogTitle
			as="h3"
			className={classNames(spacingClassNames[spacing], "font-semibold text-base text-neutral leading-5")}
		>
			{children}
		</DialogTitle>
	);
};

export const FullScreenModalCloseContent = ({
	i18n,
	className,
}: Pick<Required<ModalTitleProps>, "i18n"> & { className?: string }) => (
	<span
		className={classNames(
			"-ml-2 -mt-2 mb-2 inline-flex items-center justify-center rounded-md p-2 text-neutral hover:text-neutral",
			className,
		)}
	>
		<AccessibleIcon>
			<ArrowLeftIcon className="h-6 w-6 sm:hidden" />
		</AccessibleIcon>
		<AccessibleIcon>
			<CloseIcon className="hidden h-6 w-6 sm:block" />
		</AccessibleIcon>
		<span className="sr-only">{i18n.closeButton}</span>
	</span>
);

export const FullScreenModalTitleContent = ({
	icon,
	children,
	isModal,
}: Pick<ModalTitleProps, "icon" | "children"> & { isModal?: boolean }) => (
	<div className="flex items-center gap-3">
		{icon && (
			<div
				aria-hidden="true"
				className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-faded"
			>
				{icon}
			</div>
		)}
		<Heading As={isModal ? "h2" : "h1"} kind="h3" className={!isModal ? "sm:text-2xl sm:text-neutral" : undefined}>
			{children}
		</Heading>
	</div>
);

export const FullScreenModalSeparator = ({ className }: { className?: string }) => (
	<div aria-hidden="true" className={classNames("-mx-4 sm:-mx-6 mb-4 border-neutral-faded border-t-4", className)} />
);
